import type { BarcodeDetectionResult } from 'services/camera-strategy';
import type { QuaggaProcessResult } from 'services/barcode-decoder.service';
import { BarcodeTrackerSelector, TrackerPick, TrackerRect } from './barcode-tracker-selector';
import { HOLD_DURATION_MS } from './barcode-tracker-constants';

/** The two overlay calls the gate drives. `CsBarcodeTrackerOverlayComponent`
 *  satisfies this; tests pass a fake. */
export interface TrackerOverlayDriver {
  setIdle(animated: boolean): void;
  setDetected(rect: TrackerRect, onMorphComplete?: () => void): void;
}

/** Timer owner. The host component's `DestroyGuard` satisfies this, so every
 *  timer the gate arms is cancelled when the host is destroyed. */
export interface TrackerGateTimers {
  readonly isDestroyed: boolean;
  safeTimeout(callback: () => void, delay: number): ReturnType<typeof setTimeout>;
  cancelTimeout(timeoutId: ReturnType<typeof setTimeout>): void;
}

export interface BarcodeTrackerGateDeps {
  /** Read on every use, because the host's `@ViewChild` overlay resolves
   *  after the gate is constructed and may be absent. */
  getOverlay: () => TrackerOverlayDriver | undefined;
  /** Emits a detection to the page (the directive's `(detected)` output). */
  emitDetected: (result: BarcodeDetectionResult) => void;
  timers: TrackerGateTimers;
}

/**
 * First-morph gate + hold timer for the web barcode tracker overlay.
 *
 * Decides when the brackets may morph from the idle square onto a detected
 * barcode, when a detection may be emitted, and when the brackets fall back
 * to idle. Until the first idle→detected morph has landed, detections are
 * buffered and flushed from the overlay's morph-completion callback; after
 * that they emit per frame. A hold timer re-arms on every pick and, when it
 * expires, returns the brackets to idle and closes the gate again.
 *
 * Owned and driven by `cs-camera-view`, which alone knows whether the web
 * strategy is active and owns the container the rects are measured in. See
 * `design-docs/barcode-scanner-overlay.md`, "First-morph gate + hold timer".
 */
export class BarcodeTrackerGate {
  private selector = new BarcodeTrackerSelector();
  private isTrackingActive = false;
  private isOverlayInIdlePose = true;
  private hasFirstMorphCompleted = false;
  private pendingGatedDetection: BarcodeDetectionResult | null = null;
  private isGatedMorphInFlight = false;
  private holdTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private deps: BarcodeTrackerGateDeps) {}

  /** Reset the gate / selector state at the start of a barcode session.
   *  Called from the barcode-init path before Quagga starts so that any
   *  stale state from a previous mount or strategy switch is cleared. */
  start(): void {
    this.reset({ keepActive: true });
    this.isOverlayInIdlePose = true;
    // Force an idle paint via setIdle(false). Required for re-init paths
    // (camera flip, rotation retry) where the overlay component is the
    // same instance and may currently be on a detected pose — its own
    // ResizeObserver only fires when host dims change, not on a fresh
    // session start. Defer with safeTimeout(0) so the host's @ViewChild +
    // svg dims are available; the timer owner cancels it on teardown.
    this.deps.timers.safeTimeout(() => {
      this.deps.getOverlay()?.setIdle(false);
    }, 0);
  }

  /** Stop tracking at teardown so any late selector / gate callbacks that
   *  arrive during the (async) decoder stop drop silently. */
  stop(): void {
    this.reset({ keepActive: false });
  }

  /** Drop tracking + cancel the hold timer so the brackets stay frozen on
   *  their current rendered pose. Selector / gate flags are also reset
   *  (harmless — the page tears down right after the freeze; the next
   *  mount goes through `start()` anyway). */
  freeze(): void {
    this.reset({ keepActive: false });
  }

  /** Per-frame selector + gated overlay drive. Runs on every decoder
   *  `onProcessed` callback. `toDisplayRect` converts the selected pick
   *  from decoder space into the overlay's space, or answers null when
   *  that cannot be done this frame (the frame is then skipped). When the
   *  first idle→detected morph hasn't yet landed, the selected pick is
   *  buffered in `pendingGatedDetection` and flushed via `flushGate()`
   *  from the overlay's morph-completion callback. */
  onFrame(result: QuaggaProcessResult, toDisplayRect: (pick: TrackerPick) => TrackerRect | null): void {
    if (this.deps.timers.isDestroyed || !this.isTrackingActive) return;
    const overlay = this.deps.getOverlay();
    if (!overlay) return;

    const pick = this.selector.selectCandidate(result);
    if (!pick) return;

    const displayRect = toDisplayRect(pick);
    if (!displayRect) return;
    if (displayRect.w <= 0 || displayRect.h <= 0) return;

    this.scheduleHoldCheck();

    if (this.hasFirstMorphCompleted) {
      overlay.setDetected(displayRect);
      this.isOverlayInIdlePose = false;
      if (pick.value) {
        this.deps.emitDetected({ value: pick.value, type: pick.type });
      }
      return;
    }

    // Gate closed — buffer the pick. Only kick the morph on the first
    // gated detection; subsequent picks during the in-flight morph just
    // update the buffer (no rAF/WAAPI restart, otherwise the completion
    // event never fires at camera cadence).
    if (pick.value) {
      this.pendingGatedDetection = { value: pick.value, type: pick.type };
    }
    if (!this.isGatedMorphInFlight) {
      this.isGatedMorphInFlight = true;
      overlay.setDetected(displayRect, () => this.flushGate());
      this.isOverlayInIdlePose = false;
    }
  }

  /** Canonical reset of the first-morph-gate + selector + hold-timer
   *  state. `keepActive=true` is used at start; `keepActive=false` is used
   *  at teardown / freeze so a late morph-completion callback drops
   *  silently. */
  private reset({ keepActive }: { keepActive: boolean }): void {
    this.selector.reset();
    this.isTrackingActive = keepActive;
    this.hasFirstMorphCompleted = false;
    this.pendingGatedDetection = null;
    this.isGatedMorphInFlight = false;
    if (this.holdTimeoutId !== null) {
      this.deps.timers.cancelTimeout(this.holdTimeoutId);
      this.holdTimeoutId = null;
    }
  }

  /** Fired by the overlay's rAF morph completion callback (only on
   *  natural t≥1 — cancellation does not invoke this). Flushes the
   *  buffered detection and lifts the gate so subsequent picks emit at
   *  full cadence. */
  private flushGate(): void {
    // Late callback after teardown / freeze — drop silently. The
    // tracking-inactive path doesn't need to mutate gate flags: the
    // tracker won't run again on this branch (reset already ran on
    // freeze; the next session calls start() which resets them too).
    if (!this.isTrackingActive || this.deps.timers.isDestroyed) return;
    if (this.pendingGatedDetection) {
      this.deps.emitDetected(this.pendingGatedDetection);
    }
    this.hasFirstMorphCompleted = true;
    this.isGatedMorphInFlight = false;
    this.pendingGatedDetection = null;
  }

  /** Schedule a timer that morphs the brackets back to the idle square if
   *  no further detection arrives. Re-armed on every pick. */
  private scheduleHoldCheck(): void {
    if (this.holdTimeoutId !== null) {
      this.deps.timers.cancelTimeout(this.holdTimeoutId);
    }
    this.holdTimeoutId = this.deps.timers.safeTimeout(() => {
      this.holdTimeoutId = null;
      this.onHoldExpired();
    }, HOLD_DURATION_MS);
  }

  private onHoldExpired(): void {
    if (!this.isTrackingActive || this.isOverlayInIdlePose) return;
    this.deps.getOverlay()?.setIdle(true);
    this.isOverlayInIdlePose = true;
    // Re-arm the gate so the next lock-on goes through the same first-
    // morph guarantee as the initial one.
    this.hasFirstMorphCompleted = false;
    this.pendingGatedDetection = null;
    this.isGatedMorphInFlight = false;
  }
}
